// src/components/ServiceLeadForm.jsx
import React from 'react';
import styled from 'styled-components';
import { LeadsAPI } from '../api/public';

const Card = styled.section`
  margin: 20px 0; padding: 16px;
  border:2px solid rgba(176,141,87,.5); border-radius:16px;
  background: var(--card);
  box-shadow: 0 0 0 4px rgba(176,141,87,.15), var(--shadow);
`;
const Title = styled.h3` margin:0 0 8px; `;
const Lead  = styled.p` margin:0 0 16px; opacity:.8; `;

const Grid = styled.div`
  display:grid; gap:12px; grid-template-columns: 1fr 1fr;
  @media (max-width: 820px){ grid-template-columns:1fr; }
`;
const Field = styled.div` display:flex; flex-direction:column; gap:6px; `;
const Label = styled.label` font-size:13px; color:var(--muted); `;

// Всегда «оранжевые» поля
const Input = styled.input`
  padding:12px 14px; border-radius:12px;
  border:2px solid rgba(176,141,87,.7);
  background:var(--card); color:inherit; outline:none;
  box-shadow: 0 0 0 3px rgba(176,141,87,.15);
`;
const Text  = styled.textarea`
  grid-column: 1 / -1; min-height:120px;
  padding:12px 14px; border-radius:12px;
  border:2px solid rgba(176,141,87,.7);
  background:var(--card); color:inherit; outline:none;
  box-shadow: 0 0 0 3px rgba(176,141,87,.15);
`;

const Actions = styled.div` margin-top: 10px; display:flex; gap:10px; justify-content:flex-end; `;
const Btn = styled.button`
  padding:12px 16px; border-radius:12px; font-weight:700; cursor:pointer;
  border:1px solid var(--bdr);
  background:var(--primary); color:var(--primaryFg);
  opacity:${p=>p.disabled ? .6 : 1};
`;

const Toast = styled.div`
  position: relative;
  margin-top: 10px;
  border:1px solid #2e7d32; background: rgba(46,125,50,.08);
  color:#2e7d32; padding:10px 38px 10px 12px; border-radius:12px;
`;
const ToastErr = styled(Toast)`
  border-color:#c62828; background: rgba(198,40,40,.08); color:#c62828;
`;
const Close = styled.button`
  position:absolute; top:6px; right:6px; width:28px; height:28px;
  border-radius:8px; border:1px solid var(--bdr); background:transparent; cursor:pointer;
`;

export default function ServiceLeadForm({ type, serviceTitle }) {
  const [name,setName] = React.useState('');
  const [phone,setPhone] = React.useState('');
  const [email,setEmail] = React.useState('');
  const [message,setMessage] = React.useState('');
  const [ok, setOk] = React.useState('');
  const [err, setErr] = React.useState('');
  const [sending, setSending] = React.useState(false);

  // кулдаун при 429
  const [cooldown, setCooldown] = React.useState(0);
  React.useEffect(() => {
    if (!cooldown) return;
    const id = setInterval(() => setCooldown(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const timerRef = React.useRef(null);
  const clearTimer = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };

  const onCloseToast = () => { clearTimer(); setOk(''); setErr(''); };

  React.useEffect(() => () => clearTimer(), []);

  const send = async (e) => {
    e.preventDefault();
    if (sending || cooldown > 0) return;

    setOk(''); setErr('');
    const phoneDigits = (phone || '').replace(/\D/g,'');
    if (!name.trim() || phoneDigits.length < 6) {
      setErr('Пожалуйста, укажите имя и телефон.');
      return;
    }

    setSending(true);
    try {
      await LeadsAPI.submit({
        type,                      // ключ: тип услуги
        name,
        phone,
        email,
        comment: message,
      });
      setOk('Заявка отправлена. Мы свяжемся с вами.');
      setName(''); setPhone(''); setEmail(''); setMessage('');

      clearTimer();
      timerRef.current = setTimeout(() => setOk(''), 5000);
    } catch (e2) {
      const code = e2?.code || '';
      if (code === 'rate_limited') {
        setErr('Слишком много заявок. Пожалуйста, попробуйте через минуту.');
        // даём короткий локальный кулдаун (20 сек), чтобы юзер не спамил кнопку
        setCooldown(20);
      } else {
        setErr(String(e2?.message || e2));
      }
      clearTimer();
      timerRef.current = setTimeout(() => setErr(''), 5000);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <Title>Оставьте контакты — {serviceTitle}</Title>
      <Lead>Мы перезвоним и проконсультируем.</Lead>

      <form onSubmit={send}>
        <Grid>
          <Field>
            <Label>Имя</Label>
            <Input value={name} onChange={e=>setName(e.target.value)} placeholder="Лилия / Liliya" />
          </Field>
          <Field>
            <Label>Телефон</Label>
            <Input value={phone} onChange={e=>setPhone(e.target.value)} placeholder="+7XXXXXXXXXX" />
          </Field>

          {/* Email — на всю ширину */}
          <Field style={{ gridColumn: '1 / -1' }}>
            <Label>Email (необязательно)</Label>
            <Input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="you@email.com" />
          </Field>

          <Field style={{ gridColumn: '1 / -1' }}>
            <Label>Комментарий</Label>
            <Text value={message} onChange={e=>setMessage(e.target.value)} placeholder="Кратко опишите задачу" />
          </Field>
        </Grid>

        <Actions>
          <Btn type="submit" disabled={sending || cooldown > 0}>
            {sending ? 'Отправляем…' : (cooldown > 0 ? `Подождите ${cooldown}s` : 'Отправить')}
          </Btn>
        </Actions>
      </form>

      {ok && (
        <Toast>
          {ok}
          <Close onClick={onCloseToast} aria-label="Закрыть">×</Close>
        </Toast>
      )}
      {err && (
        <ToastErr>
          {err}
          <Close onClick={onCloseToast} aria-label="Закрыть">×</Close>
        </ToastErr>
      )}
    </Card>
  );
}
